<!-- eslint-disable vue/no-v-html -->
<template>
  <div
    :class="[
      'flex min-h-[24px] items-start justify-between gap-3 text-gray-900',
      customField.sensitive ? 'cursor-pointer rounded-md transition-colors hover:bg-gray-50' : '',
    ]"
    @click="customField.sensitive ? emit('toggleSensitive') : null"
  >
    <span
      v-if="customField.sensitive && !isRevealed"
      class="flex items-center gap-1 text-[15px] leading-6 text-gray-900"
    >
      ••••••••••••••
    </span>

    <template v-else>
      <span v-if="value && customField.field_type === 'multiple_options'" class="flex flex-wrap items-center gap-1.5">
        <Badge
          v-for="(option, index) in value"
          :key="index"
          :variant="'gray'"
          class="border-0 bg-gray-100 text-gray-700"
        >
          {{ option }}
        </Badge>
      </span>
      <div
        v-else-if="customField.field_type === 'rich_text'"
        class="user-generated-content custom-field-rich-text w-full min-w-0 flex-1 break-words whitespace-pre-line"
        v-html="sanitize(value as string)"
      ></div>
      <span v-else class="flex items-center gap-1 text-[15px] leading-6 text-gray-900">
        {{ displayValue }}&nbsp;
        <a v-if="isUrl" :href="value as string" target="_blank" rel="noopener noreferrer">
          <vue-feather type="external-link" size="14" class="text-gray-400" />
        </a>
      </span>
    </template>

    <span v-if="customField.sensitive" class="mt-0.5 flex items-center gap-1 text-gray-400">
      <vue-feather v-if="isRevealed" type="eye" size="14" />
      <vue-feather v-else type="eye-off" size="14" />
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { formatStandardDate, formatCurrencyToHuman } from '@/utilities/format';
import { sanitizeRichText } from '@/utilities/sanitization';
import type { CustomFieldEntity } from '@/api/entity_types/CustomField';
import Badge from '@/components/ui/badge/Badge.vue';

type Props = {
  customField: CustomFieldEntity;
  value: string | string[];
  isRevealed: boolean;
};

const props = defineProps<Props>();

const emit = defineEmits<{
  toggleSensitive: [];
}>();

const displayValue = computed(() => {
  if (props.customField.sensitive && !props.isRevealed) {
    return '••••••••••••••';
  } else if (props.customField.field_type === 'date' && props.value) {
    return formatStandardDate(props.value as string);
  } else if (props.customField.field_type === 'currency' && props.value) {
    return formatCurrencyToHuman(props.value as string, 2);
  } else if (props.customField.field_type === 'percentage' && props.value) {
    return `${props.value}%`;
  }

  if (!props.value || (Array.isArray(props.value) && props.value.length === 0)) {
    return '—';
  }

  return props.value;
});

const isUrl = computed(() => {
  const regex = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/;
  return regex.test(props.value as string);
});

const sanitize = (html: string) => sanitizeRichText(html);
</script>

<style scoped>
@reference "tailwindcss";

.custom-field-rich-text {
  @apply text-[15px] leading-6 text-gray-900;
}

.custom-field-rich-text:deep(p) {
  @apply my-0;
}

.custom-field-rich-text:deep(p + p) {
  @apply mt-2;
}
</style>
